import { LSelect } from "./ui/LSelect";
import { useEffect, useState, useCallback } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable, Badge } from "@/components/common/DataTable";
import { loyaltyService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import RuleBuilder from "./RuleBuilder";
import { Plus, Pencil, Trash2, X, Loader2, Sparkles } from "lucide-react";

const STATUS_COLOR = { Active: "green", Inactive: "slate" };
const newRule = () => ({ name: "", programId: "", status: "Active", priority: 10, trigger: { event: "purchase" }, match: "all", conditions: [], actions: [{ type: "award_points", value: "" }] });

export default function RewardRulesPage() {
  const { hasPermission } = useAuth();
  const [rows, setRows] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [rule, setRule] = useState(newRule());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, p] = await Promise.all([loyaltyService.rules(), loyaltyService.programs()]);
      setRows(r.items || []); setPrograms(p.items || []);
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); loyaltyService.ruleCatalog().then(setCatalog).catch(() => {}); }, [load]);

  const openCreate = () => { setEditing(null); setRule(newRule()); setOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setRule({ name: r.name, programId: r.programId || "", status: r.status, priority: r.priority ?? 10,
      trigger: r.trigger || { event: "purchase" }, match: r.match || "all", conditions: r.conditions || [], actions: r.actions || [] });
    setOpen(true);
  };

  const save = async () => {
    if (!rule.name.trim()) { toast.error("Rule name is required"); return; }
    if (!rule.actions.length) { toast.error("Add at least one action"); return; }
    setSaving(true);
    const payload = { ...rule, programId: rule.programId || null };
    try {
      if (editing) await loyaltyService.updateRule(editing.id, payload);
      else await loyaltyService.createRule(payload);
      toast.success(editing ? "Rule updated" : "Rule created");
      setOpen(false); load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete rule "${r.name}"?`)) return;
    try { await loyaltyService.deleteRule(r.id); toast.success("Rule deleted"); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const progName = (id) => programs.find((p) => p.id === id)?.name || "—";
  const canManage = hasPermission("LOYALTY.CREATE") || hasPermission("LOYALTY.UPDATE");

  const columns = [
    { key: "name", header: "Rule", render: (r) => <span className="font-medium text-foreground">{r.name}</span> },
    { key: "trigger", header: "Trigger", render: (r) => <Badge color="blue">{catalog?.triggers?.find((t) => t.value === r.trigger?.event)?.label || r.trigger?.event}</Badge> },
    { key: "actions", header: "Actions", render: (r) => <span className="text-sm text-muted-foreground">{(r.actions || []).length} action(s)</span> },
    { key: "program", header: "Program", render: (r) => <span className="text-sm">{progName(r.programId)}</span> },
    { key: "priority", header: "Priority", render: (r) => <span className="text-sm">{r.priority}</span> },
    { key: "status", header: "Status", render: (r) => <Badge color={STATUS_COLOR[r.status] || "slate"}>{r.status}</Badge> },
    { key: "act", header: "", render: (r) => canManage && (
      <div className="flex gap-1 justify-end">
        <button onClick={() => openEdit(r)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground" data-testid={`rule-edit-${r.id}`}><Pencil className="h-4 w-4" /></button>
        {hasPermission("LOYALTY.ADMIN") && <button onClick={() => remove(r)} className="p-1.5 rounded-lg hover:bg-destructive/10 text-destructive" data-testid={`rule-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>}
      </div>
    ) },
  ];

  return (
    <div data-testid="reward-rules-page">
      <PageHeader title="Reward Rules" subtitle="Visual rule builder — design earn & reward logic without code"
        actions={hasPermission("LOYALTY.CREATE") && (
          <button onClick={openCreate} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90" data-testid="rule-create-button">
            <Plus className="h-4 w-4" /> New Rule
          </button>
        )} />

      <DataTable testid="reward-rules-table" rows={rows} loading={loading} columns={columns} empty="No reward rules yet — build your first rule." />

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="rule-modal">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="relative bg-card border border-border rounded-2xl shadow-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-card z-10">
              <div className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" /><h3 className="font-semibold text-foreground">{editing ? "Edit Rule" : "New Reward Rule"}</h3></div>
              <button onClick={() => setOpen(false)} data-testid="rule-modal-close"><X className="h-5 w-5 text-muted-foreground hover:text-foreground" /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-foreground mb-1">Rule Name</label>
                  <input value={rule.name} onChange={(e) => setRule({ ...rule, name: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="rule-name-input" placeholder="e.g. Birthday Bonus" /></div>
                <div><label className="block text-sm font-medium text-foreground mb-1">Program (optional)</label>
                  <LSelect value={rule.programId} onChange={(e) => setRule({ ...rule, programId: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="rule-program-select">
                    <option value="">— None —</option>
                    {programs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </LSelect></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-foreground mb-1">Priority</label>
                  <input type="number" value={rule.priority} onChange={(e) => setRule({ ...rule, priority: Number(e.target.value) })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" /></div>
                <div><label className="block text-sm font-medium text-foreground mb-1">Status</label>
                  <LSelect value={rule.status} onChange={(e) => setRule({ ...rule, status: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm">
                    <option value="Active">Active</option><option value="Inactive">Inactive</option>
                  </LSelect></div>
              </div>
              <RuleBuilder value={rule} onChange={setRule} catalog={catalog} />
            </div>
            <div className="px-6 py-4 border-t border-border flex justify-end gap-2 sticky bottom-0 bg-card">
              <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted">Cancel</button>
              <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90 disabled:opacity-50" data-testid="rule-save-button">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} {editing ? "Save Changes" : "Create Rule"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
